import {AssemblySetup, AssemblyNode} from "./AssemblyTypes";
import {ASSEMBLY_RECIPES, ASSEMBLY_RECIPE_LIST, AssemblyRecipe} from "../State/AssemblyRecipes";

/**
 * Find all assembly setups that use a given resource as either:
 * - A source node (direct itemId match)
 * - An assembler recipe ingredient
 * - An assembler recipe output
 * - A connection item
 */
export function findSetupsUsingResource(resourceId: string, setups: AssemblySetup[]): AssemblySetup[] {
    const matchingSetups: AssemblySetup[] = [];

    setups.forEach(setup => {
        let usesResource = false;

        // Check each node in the setup
        for (const node of setup.nodes) {
            // Check source nodes
            if (node.type === "source" && node.itemId === resourceId) {
                usesResource = true;
                break;
            }

            // Check assembler nodes
            if (node.type === "assembler" && node.recipeId) {
                const recipe = ASSEMBLY_RECIPES[node.recipeId];
                if (recipe) {
                    // Check if resource is an ingredient
                    const isIngredient = recipe.ingredients.some(ing => ing.itemId === resourceId);
                    // Check if resource is the output
                    const isOutput = recipe.result.itemId === resourceId;

                    if (isIngredient || isOutput) {
                        usesResource = true;
                        break;
                    }
                }
            }
        }

        // Also check connections (defensive check)
        if (!usesResource) {
            usesResource = setup.connections.some(conn => conn.itemId === resourceId);
        }

        if (usesResource) {
            matchingSetups.push(setup);
        }
    });

    return matchingSetups;
}

/**
 * Generates a random ID string for assembly nodes and setups.
 * Uses base-36 encoding of a random number for compact, readable IDs.
 *
 * @returns A random 7-character alphanumeric string
 *
 * @example
 * const nodeId = generateNodeId();
 * // Returns something like "k2j9x4a"
 */
export const generateNodeId = (): string => {
    return Math.random().toString(36).slice(2, 9);
};

/**
 * Creates a lookup map from recipe IDs to recipe objects for fast access.
 *
 * @returns Record mapping recipe IDs to AssemblyRecipe objects
 *
 * @example
 * const lookup = createRecipeLookup();
 * const recipe = lookup["circuit-recipe"];
 */
export const createRecipeLookup = (): Record<string, AssemblyRecipe> => {
    return ASSEMBLY_RECIPE_LIST.reduce<Record<string, AssemblyRecipe>>((acc, recipe) => {
        acc[recipe.id] = recipe;
        return acc;
    }, {});
};

/**
 * Determines the output item ID for a given assembly node.
 *
 * @param node - The assembly node to check
 * @param recipeLookup - Optional recipe lookup map (will be created if not provided)
 * @returns The item ID this node outputs, or undefined if it produces no output
 *
 * @example
 * const outputId = getNodeOutputItem(sourceNode);
 * // Returns "ironplate" for an iron plate source node
 *
 * const outputId2 = getNodeOutputItem(assemblerNode, recipeLookup);
 * // Returns the result itemId from the assembler's recipe
 */
export const getNodeOutputItem = (
    node: AssemblyNode,
    recipeLookup?: Record<string, AssemblyRecipe>
): string | undefined => {
    if (node.type === "source") {
        return node.itemId;
    }
    if (node.type === "assembler" && node.recipeId) {
        const lookup = recipeLookup ?? createRecipeLookup();
        return lookup[node.recipeId]?.result.itemId;
    }
    return undefined;
};

/**
 * Checks if an assembler node has all required ingredient connections.
 *
 * @param setup - The assembly setup containing the node and connections
 * @param node - The assembler node to check
 * @param recipeLookup - Optional recipe lookup map (will be created if not provided)
 * @returns True if the node has incoming connections for all required ingredients
 *
 * @example
 * const hasInputs = nodeHasIngredients(setup, assemblerNode, recipeLookup);
 * // Returns true if all recipe ingredients have incoming connections
 */
export const nodeHasIngredients = (
    setup: AssemblySetup,
    node: AssemblyNode,
    recipeLookup?: Record<string, AssemblyRecipe>
): boolean => {
    if (node.type !== "assembler" || !node.recipeId) return false;

    const lookup = recipeLookup ?? createRecipeLookup();
    const recipe = lookup[node.recipeId];
    if (!recipe) return false;

    const incoming = setup.connections.filter((conn) => conn.toId === node.id);
    return recipe.ingredients.every((ingredient) =>
        incoming.some((conn) => conn.itemId === ingredient.itemId)
    );
};
